import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Trophy, BarChart3, Sparkles, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatTile = ({ label, value, sub, color = 'blue', icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      {Icon && <Icon size={16} className={`text-${color}-500`} />}
    </div>
    <div className={`text-2xl font-bold text-${color}-700`}>{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

export default function BenchmarkingTab() {
  const [summary, setSummary] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [bands, setBands] = useState(null);
  const [topSchools, setTopSchools] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const h = authHeader();
        const [s, g, b, t] = await Promise.all([
          axios.get(`${API}/benchmarking/summary`, { headers: h }),
          axios.get(`${API}/benchmarking/district-growth`, { headers: h }),
          axios.get(`${API}/benchmarking/grade-band-medians`, { headers: h }),
          axios.get(`${API}/benchmarking/top-schools?limit=10`, { headers: h }),
        ]);
        setSummary(s.data);
        setGrowth(g.data);
        setBands(b.data);
        setTopSchools(t.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-gray-500" data-testid="benchmarking-loading">Loading benchmarking data…</div>;
  if (error) return (
    <div className="p-6 bg-red-50 border border-red-200 rounded text-red-700 flex items-start gap-2" data-testid="benchmarking-error">
      <AlertCircle size={18} className="mt-0.5" /> <span>{error}</span>
    </div>
  );

  const maxGrowth = Math.max(0.5, ...(growth?.districts || []).map(d => Math.abs(d.growth)));

  return (
    <div className="space-y-8" data-testid="benchmarking-tab">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-blue-600" size={22} />
          <div>
            <h2 className="text-xl font-bold text-gray-900">District Benchmarking</h2>
            <p className="text-sm text-gray-600">Cross-district growth leaderboard, grade-band medians, and top schools for case studies.</p>
          </div>
        </div>

        {/* Platform summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <StatTile label="Districts" value={summary?.total_districts || 0} icon={BarChart3} color="blue" />
          <StatTile label="Schools" value={summary?.total_schools || 0} icon={BarChart3} color="emerald" />
          <StatTile label="Students" value={summary?.total_students?.toLocaleString() || 0} icon={BarChart3} color="amber" />
          <StatTile label="Platform Avg Proficiency" value={summary?.platform_avg_wida?.toFixed(2) || '0.00'} sub={`Median ${summary?.platform_median_wida?.toFixed(2) || '0.00'}`} icon={TrendingUp} color="purple" />
        </div>
      </div>

      {/* District growth leaderboard */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-600" />
            District Growth Leaderboard (12mo)
          </h3>
          <div className="text-sm text-gray-600">
            Platform: <span className="font-semibold text-emerald-700">+{growth?.platform_growth?.toFixed(2) || '0.00'}</span> proficiency pts
          </div>
        </div>
        <div className="space-y-2">
          {growth?.districts?.map((d, idx) => (
            <div key={d.district_id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={`district-row-${idx}`}>
              <div className="w-8 text-right text-gray-400 font-mono text-sm">#{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{d.district_name}</div>
                <div className="text-xs text-gray-500">{d.student_count} students · {d.assessments} assessments · Baseline {d.baseline_wida.toFixed(2)} → Current {d.current_wida.toFixed(2)}</div>
              </div>
              <div className="w-48 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full ${d.growth >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, (Math.abs(d.growth) / maxGrowth) * 100)}%` }} />
              </div>
              <div className={`w-16 text-right font-bold ${d.growth >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                {d.growth >= 0 ? '+' : ''}{d.growth.toFixed(2)}
              </div>
            </div>
          ))}
          {(!growth?.districts || growth.districts.length === 0) && (
            <div className="text-center text-gray-500 py-4">No district data yet.</div>
          )}
        </div>
      </div>

      {/* Grade band medians */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <BarChart3 size={18} className="text-blue-600" />
          Grade Band Medians (Platform-wide)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-3 py-2 border-b border-gray-200">Grade Band</th>
                <th className="px-3 py-2 border-b border-gray-200">Students</th>
                <th className="px-3 py-2 border-b border-gray-200">Composite Median</th>
                <th className="px-3 py-2 border-b border-gray-200">Listening</th>
                <th className="px-3 py-2 border-b border-gray-200">Speaking</th>
                <th className="px-3 py-2 border-b border-gray-200">Reading</th>
                <th className="px-3 py-2 border-b border-gray-200">Writing</th>
              </tr>
            </thead>
            <tbody>
              {bands?.bands?.map((b, idx) => (
                <tr key={b.band} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`band-row-${b.band}`}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{b.band}</td>
                  <td className="px-3 py-2 text-gray-700">{b.n.toLocaleString()}</td>
                  <td className="px-3 py-2 font-bold text-blue-700">{b.composite_median.toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-700">{b.listening_avg.toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-700">{b.speaking_avg.toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-700">{b.reading_avg.toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-700">{b.writing_avg.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top schools */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Trophy size={18} className="text-amber-600" />
          Top 10 Schools — Case Study Candidates
        </h3>
        <div className="space-y-2">
          {topSchools?.schools?.map((s, idx) => (
            <div key={s.school_id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={`top-school-${idx}`}>
              <div className="w-8 text-right font-mono text-sm text-amber-600 font-bold">#{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{s.school_name}</div>
                <div className="text-xs text-gray-500">{s.assessments} assessments · median {s.median_wida.toFixed(2)}</div>
              </div>
              <div className="font-bold text-blue-700 text-lg">{s.avg_wida.toFixed(2)}</div>
            </div>
          ))}
          {(!topSchools?.schools || topSchools.schools.length === 0) && (
            <div className="text-center text-gray-500 py-4">Not enough data yet (need ≥5 assessments per school).</div>
          )}
        </div>
      </div>
    </div>
  );
}
